import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import * as googleMapsApi from '../utils/googleMapsApi';
import './GoogleMapsLocationPicker.css';

/**
 * Google Maps Location Picker Component
 * 
 * Displays an interactive Google Maps interface where users can:
 * - Search for locations using Places Autocomplete
 * - Click on the map to select a location
 * - See a marker at the selected location
 * - View location details
 */
const GoogleMapsLocationPicker = ({ onLocationSelect, initialLocation, placeholder }) => {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const autocompleteInputRef = useRef(null);
  const initialLocationProcessedRef = useRef(false); // Track if initial location has been processed
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [autocomplete, setAutocomplete] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    // Check if script is already loaded
    if (window.google && window.google.maps) {
      setIsScriptLoaded(true);
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Wait for it to load
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          setIsScriptLoaded(true);
          clearInterval(checkInterval);
        }
      }, 100);
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${config.GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    script.onerror = () => {
      setError('Failed to load Google Maps. Please check your internet connection.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup is handled by the script itself
    };
  }, []);

  // Initialize map once script is loaded
  useEffect(() => {
    if (!isScriptLoaded || !mapRef.current || map) return; // Don't re-initialize if map already exists

    try {
      // Default center (can be user's location or a default city)
      const defaultCenter = initialLocation 
        ? { lat: initialLocation.latitude, lng: initialLocation.longitude }
        : { lat: 37.7749, lng: -122.4194 }; // San Francisco default

      // Create map
      const googleMap = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: initialLocation ? 15 : 10,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
      });

      setMap(googleMap);

      // Create marker
      const googleMarker = new window.google.maps.Marker({
        map: googleMap,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
        position: initialLocation ? defaultCenter : null,
      });

      setMarker(googleMarker);

      // Add click listener to map
      googleMap.addListener('click', (event) => {
        handleMapClick(event.latLng, googleMarker, googleMap);
      });

      // Add drag listener to marker
      googleMarker.addListener('dragend', (event) => {
        handleMapClick(event.latLng, googleMarker, googleMap);
      });

      // If initial location, fetch details (only once)
      if (initialLocation && initialLocation.latitude && initialLocation.longitude && !initialLocationProcessedRef.current) {
        initialLocationProcessedRef.current = true;
        handleMapClick(defaultCenter, googleMarker, googleMap);
      }

    } catch (err) {
      console.error('Error initializing map:', err);
      setError('Failed to initialize map. Please refresh the page.');
    }
  }, [isScriptLoaded]); // Remove initialLocation from dependencies

  // Initialize autocomplete
  useEffect(() => {
    if (!isScriptLoaded || !autocompleteInputRef.current || !map) return;

    try {
      const autocompleteInstance = new window.google.maps.places.Autocomplete(
        autocompleteInputRef.current,
        {
          fields: ['place_id', 'geometry', 'name', 'formatted_address'],
        }
      );

      // Bind autocomplete to map bounds
      autocompleteInstance.bindTo('bounds', map);

      // Add listener for place selection
      autocompleteInstance.addListener('place_changed', () => {
        const place = autocompleteInstance.getPlace();

        if (!place.geometry || !place.geometry.location) {
          setError('No details available for this location');
          return;
        }

        // Update map and marker
        map.setCenter(place.geometry.location);
        map.setZoom(15);
        marker.setPosition(place.geometry.location);
        marker.setVisible(true);

        // Fetch full details
        handlePlaceSelect(place);
      });

      setAutocomplete(autocompleteInstance);

    } catch (err) {
      console.error('Error initializing autocomplete:', err);
      setError('Failed to initialize search. Please refresh the page.');
    }
  }, [isScriptLoaded, map, marker]);

  /**
   * Handle map click - reverse geocode and fetch place details
   */
  const handleMapClick = async (latLng, markerInstance, mapInstance) => {
    try {
      setLoading(true);
      setError(null);

      // Update marker position
      markerInstance.setPosition(latLng);
      markerInstance.setVisible(true);
      mapInstance.panTo(latLng);

      const lat = typeof latLng.lat === 'function' ? latLng.lat() : latLng.lat;
      const lng = typeof latLng.lng === 'function' ? latLng.lng() : latLng.lng;

      // Use Geocoder to get place details from coordinates
      const geocoder = new window.google.maps.Geocoder();
      
      geocoder.geocode({ location: { lat, lng } }, async (results, status) => {
        if (status === 'OK' && results[0]) {
          const place = results[0];
          
          // Fetch full details using our backend API
          await fetchPlaceDetails(place.place_id, lat, lng);
        } else {
          setError('Could not find location details for this point');
        }
      });

    } catch (err) {
      console.error('Error handling map click:', err);
      setError('Failed to fetch location details');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle place selection from autocomplete
   */
  const handlePlaceSelect = async (place) => {
    try {
      setLoading(true);
      setError(null);

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      // Fetch full details using our backend API
      await fetchPlaceDetails(place.place_id, lat, lng);

    } catch (err) {
      console.error('Error handling place selection:', err);
      setError('Failed to fetch location details');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch place details from backend API
   */
  const fetchPlaceDetails = async (placeId, latitude, longitude) => {
    try {
      const token = await getAccessToken();
      
      // Fetch place details with timezone from backend
      const details = await googleMapsApi.getPlaceDetails(placeId, token);

      if (details) {
        // Flatten the location object if it exists (backend returns nested location)
        const flattenedDetails = {
          ...details,
          latitude: details.location?.latitude || latitude,
          longitude: details.location?.longitude || longitude,
        };

        // Remove the nested location object to avoid confusion
        delete flattenedDetails.location;

        setSelectedPlace(flattenedDetails);
        
        // Call parent callback
        if (onLocationSelect) {
          onLocationSelect(flattenedDetails);
        }
      } else {
        // Fallback: create basic details from coordinates
        const timezone = await googleMapsApi.getTimezone(latitude, longitude, token);
        
        const basicDetails = {
          placeId,
          latitude,
          longitude,
          timezone: timezone?.timezone || 'UTC',
          name: 'Selected Location',
          formattedAddress: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        };

        setSelectedPlace(basicDetails);
        
        if (onLocationSelect) {
          onLocationSelect(basicDetails);
        }
      }

    } catch (err) {
      console.error('Error fetching place details:', err);
      setError('Failed to fetch location details. Please try again.');
    }
  };

  /**
   * Clear selection
   */
  const handleClear = () => {
    setSelectedPlace(null);
    setError(null);
    
    if (marker) {
      marker.setVisible(false);
    }
    
    if (autocompleteInputRef.current) {
      autocompleteInputRef.current.value = '';
    }

    if (onLocationSelect) {
      onLocationSelect(null);
    }
  };

  /**
   * Use current location
   */
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latLng = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        if (map && marker) {
          map.setCenter(latLng);
          map.setZoom(15);
          handleMapClick(latLng, marker, map);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setError('Unable to get your location. Please search manually.');
        setLoading(false);
      }
    );
  };

  return (
    <div className="google-maps-location-picker">
      {/* Search and Controls */}
      <div className="map-controls">
        <div className="search-container">
          <input
            ref={autocompleteInputRef}
            type="text"
            className="map-search-input"
            placeholder={placeholder || "Search for a location..."}
            disabled={!isScriptLoaded}
          />
          <button
            type="button"
            className="use-location-btn"
            onClick={handleUseCurrentLocation}
            disabled={loading || !isScriptLoaded}
            title="Use my current location"
          >
            📍 Use My Location
          </button>
          {selectedPlace && (
            <button
              type="button"
              className="clear-location-btn"
              onClick={handleClear}
              disabled={loading}
            >
              ✕ Clear
            </button>
          )}
        </div>

        {error && (
          <div className="map-error-message">
            ⚠️ {error}
          </div>
        )}

        {loading && (
          <div className="map-loading-message">
            🔄 Loading location details...
          </div>
        )}
      </div>

      {/* Map Container */}
      <div className="map-container-wrapper">
        <div ref={mapRef} className="google-map-display" />
        
        {!isScriptLoaded && (
          <div className="map-loading-overlay">
            <div className="map-loading-spinner">Loading map...</div>
          </div>
        )}
      </div>

      {/* Selected Place Details */}
      {selectedPlace && (
        <div className="selected-place-details">
          <div className="place-details-header">
            <h4>✅ Selected Location</h4>
          </div>
          
          <div className="place-details-content">
            <div className="place-detail-row">
              <strong>Name:</strong>
              <span>{selectedPlace.name}</span>
            </div>
            
            <div className="place-detail-row">
              <strong>Address:</strong>
              <span>{selectedPlace.formattedAddress}</span>
            </div>

            {selectedPlace.city && (
              <div className="place-detail-row">
                <strong>City:</strong>
                <span>{selectedPlace.city}, {selectedPlace.state} {selectedPlace.postalCode}</span>
              </div>
            )}

            <div className="place-detail-row">
              <strong>Coordinates:</strong>
              <span>{selectedPlace.latitude?.toFixed(6)}, {selectedPlace.longitude?.toFixed(6)}</span>
            </div>

            <div className="place-detail-row">
              <strong>Timezone:</strong>
              <span className="timezone-badge">{selectedPlace.timezone}</span>
            </div>

            {selectedPlace.rating && (
              <div className="place-detail-row">
                <strong>Rating:</strong>
                <span>⭐ {selectedPlace.rating} ({selectedPlace.userRatingsTotal} reviews)</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      {!selectedPlace && isScriptLoaded && !loading && (
        <div className="map-instructions">
          <p>🔍 <strong>Search</strong> for a location using the search box above</p>
          <p>📍 <strong>Click</strong> anywhere on the map to select a location</p>
          <p>🎯 <strong>Use My Location</strong> to select your current position</p>
        </div>
      )}
    </div>
  );
};

export default GoogleMapsLocationPicker;

